/**
 * Core: App handler
 * -----------------------------------------------------------------------------
 * Any injected <script> tags will break the Vue container. Because of this,
 * app proxy pages and app blocks need a workaround.
 * - Moves specified components to the end of the HTML body on app proxy pages.
 * - Move sections containing app blocks outside the Vue container.
 * - Return sections containing app blocks to their original placements after
 *   Vue has finished rendering.
 * - Original code by Jenny Liu, updated by Andrew Amistad.
 *
 */
import classes from '~/config/classes'
import { values as timings } from '~/config/timings'

/**
 * DOM selectors.
 */
const selectors = {
  placeholder: '[js-app-handler="placeholder"]',
  sections: '.shopify-section',
}

/**
 * Create a new app handler object.
 */
export default () => {

  /**
   * Set global variables.
   */
  const appPlacements = {}

  /**
   * Run functionality before Vue instance.
   */
  function beforeVueInit() {
    handleAppSections()
  }

  /**
   * Handle sections containing app blocks.
   */
  function handleAppSections() {

    /**
     * Go through each section, check to see if it has children and if one of
     * those children has 'shopify-app-block' class.
     */
    const appSections = [...document.querySelectorAll(selectors.sections)].filter((section) => {
      if (!section.children) {
        return false
      }

      return [...section.children].find((child) => {
        return child.classList?.contains('shopify-app-block')
      })
    })

    /**
     * Store app section placement.
     */
    appSections.forEach((section) => {
      appPlacements[section.id] = {
        parentElementId: section.parentElement?.id,
        previousElementId: section.previousElementSibling?.id,
      }
    })

    /**
     * Move app section to the end of <body>.
     */
    appSections.forEach((section) => {
      teleportContent(section, document.body)
    })
  }

  /**
   * Teleport content to a different container.
   * @param {HTMLElement} element - Element to teleport.
   * @param {HTMLElement} destination - Element to teleport into.
   * @param {Boolean} addClass - Add is-moved class to element?
   */
  function teleportContent(element, destination, addClass = false) {
    if (!element) {
      return
    }

    if (addClass) {
      element.classList.add(classes.moved)
    }

    destination.appendChild(element)
  }

  /**
   * Run functionality after Vue instance.
   */
  function afterVueInit() {
    const placeholder = document.querySelector(selectors.placeholder)

    if (isAppPage() && placeholder) {
      moveElements(placeholder)
      removePlaceholder(placeholder)
    }

    resetAppSectionPlacements()
  }

  /**
   * Check if page is an app proxy page.
   * - Or is a captcha page.
   * @returns {Boolean}
   */
  function isAppPage() {
    if (cnvs.page.type === 'captcha') {
      return true
    }

    const appPages = [
      '/a/',
      '/apps/',
      '/community/',
      '/tools/',
    ]

    return appPages.some((page) => cnvs.request.path.includes(page))
  }

  /**
   * Move elements to end of the body if it's an app proxy page.
   * - Use data-elements on app-handler element to list IDs of elements to move.
   * @param {HTMLElement} placeholder - App handler placeholder element.
   */
  function moveElements(placeholder) {
    const elementIds = placeholder?.dataset.elements?.split(',')

    if (!elementIds.length) {
      return
    }

    elementIds.forEach((id) => {
      teleportContent(document.getElementById(id), document.body, true)
    })
  }

  /**
   * Remove app handler placeholder.
   * - Placeholder covers the page until the page is finished shifting.
   * - Fade it out, then remove it.
   * @param {HTMLElement} placeholder - App handler placeholder element.
   */
  function removePlaceholder(placeholder) {
    placeholder.classList.remove(classes.visible)

    setTimeout(() => {
      placeholder.remove()
    }, timings.normal)
  }

  /**
   * Move all app sections back to their original placements.
   * - Move it after its original previous sibling if there was one.
   * - Otherwise move to start of its original parent.
   */
  function resetAppSectionPlacements() {
    if (!Object.keys(appPlacements).length) {
      return
    }

    Object.entries(appPlacements).forEach(([sectionId, value]) => {
      const section = document.getElementById(sectionId)
      const { parentElementId, previousElementId } = value

      const previousElement = document.getElementById(previousElementId)
      const parentElement = document.getElementById(parentElementId)

      if (previousElement) {
        previousElement.insertAdjacentElement('afterend', section)
        return
      }

      if (!parentElement) {
        return
      }

      parentElement.insertAdjacentElement('afterbegin', section)
    })
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    beforeVueInit,
    afterVueInit,
  })
}
